import { useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';
import {
  useAddWatchKnownListMutation,
  useApproveWatchInvestigationMutation,
  useCancelWatchInvestigationMutation,
  useContinueWatchInvestigationMutation,
  useCreateWatchCompaniesMutation,
  useCreateWatchScheduleMutation,
  useGetWatchCompaniesQuery,
  useGetWatchCompanyQuery,
  useGetWatchIncidentQuery,
  useGetWatchIncidentsQuery,
  useGetWatchIssueChatsQuery,
  useGetWatchSchedulesQuery,
  useGetWatchTopicsQuery,
  useImportWatchCompaniesMutation,
  useOpenWatchCreateChatMutation,
  useOpenWatchDossierChatMutation,
  useOpenWatchIssueChatMutation,
  useRetryWatchInvestigationMutation,
  useSendWatchDigestMutation,
} from 'store/slices/watchTower/apis/watchTowerApi';

import { emptyWatchTowerState, upsertById } from './emptyWatchTowerState';
import {
  mapWatchTowerLive,
  sessionCreateChat,
  sessionDossierChat,
  sessionIssueChat,
} from './mapWatchTowerLive';
import {
  type CreateWatchScheduleInput,
  defaultTargetKinds,
  targetsAvailableForKind,
} from './scheduleWatchAgents';

import type { IWatchCreateScheduleRequest } from 'store/slices/watchTower/interfaces/IWatchTower';
import type {
  WatchAiChat,
  WatchFinding,
  WatchIssueChat,
  WatchTowerMockState,
} from './types';

export interface WatchTowerLiveActions {
  importFile: (payload: unknown) => Promise<void>,
  openCreateChat: () => Promise<WatchAiChat>,
  createCompanies: (seed: unknown, confirmedExistingUids: string[]) => Promise<void>,
  openIssueChat: (finding: WatchFinding) => Promise<WatchIssueChat>,
  openDossierChat: (companyId: number, companyName: string) => Promise<WatchAiChat>,
  approveInvestigation: (companyId: number) => Promise<void>,
  cancelInvestigation: (companyId: number) => Promise<void>,
  continueInvestigation: (companyId: number, runId: number) => Promise<void>,
  retryInvestigation: (companyId: number) => Promise<void>,
  addKnownList: (input: {
    publicId: string,
    buyerWatchCompanyId: number,
    supplierWatchCompanyId: number,
    watchProductId?: number | null,
  }) => Promise<void>,
  createSchedules: (input: CreateWatchScheduleInput, state: WatchTowerMockState) => Promise<void>,
  sendDigest: () => Promise<void>,
}

function padHour(hourOfDay: number): string {
  return `${String(hourOfDay).padStart(2, '0')}:00`;
}

function liveScheduleBody(
  input: CreateWatchScheduleInput,
  state: WatchTowerMockState,
  companyId: number,
  agentConfigId: number,
): IWatchCreateScheduleRequest {
  const company = state.watchCompanies.find((item) => item.id === companyId);
  const kinds = defaultTargetKinds(input.type);
  const targets = kinds.flatMap((kind) => {
    const available = company ? targetsAvailableForKind(company, kind) : [];
    if (!available.length && kind === 'company') {
      return [{ targetKind: 'company', watchCompanyId: companyId, targetId: companyId }];
    }
    return available.map((item) => ({
      targetKind: kind,
      watchCompanyId: companyId,
      targetId: item.id,
    }));
  });
  return {
    title: `${company?.name || companyId} ${input.type}`,
    type: input.type,
    subType: input.subType,
    agentConfigId,
    timezone: input.timezone,
    frequency: input.frequency,
    hourOfDay: input.hourOfDay,
    timeOfDay: padHour(input.hourOfDay),
    startDate: (input.now || new Date()).toISOString().slice(0, 10),
    targets,
  };
}

function mergeLiveState(
  mapped: WatchTowerMockState,
  session: WatchTowerMockState | null,
): WatchTowerMockState {
  if (!session) {
    return mapped;
  }
  return {
    ...mapped,
    createChats: upsertById(mapped.createChats, session.createChats),
    issueChats: upsertById(mapped.issueChats, session.issueChats),
    createChatMessages: session.createChatMessages,
    issueChatMessages: session.issueChatMessages,
  };
}

export default function useWatchTowerLive() {
  const location = useLocation();
  const params = useParams();
  const companyMatch = matchPath({ path: AppRoutes.watchTowerCompany, end: true }, location.pathname);
  const findingMatch = matchPath({ path: AppRoutes.marketingOpsFinding, end: true }, location.pathname);
  const companyId = companyMatch ? Number(params.id) || 0 : 0;
  const publicId = findingMatch ? (params.publicId || '') : '';

  const companiesQuery = useGetWatchCompaniesQuery();
  const companyQuery = useGetWatchCompanyQuery(companyId, { skip: !companyId });
  const { refetch: refetchOpenCompany } = companyQuery;
  const topicsQuery = useGetWatchTopicsQuery();
  const schedulesQuery = useGetWatchSchedulesQuery();
  const incidentsQuery = useGetWatchIncidentsQuery();
  const incidentQuery = useGetWatchIncidentQuery(publicId, { skip: !publicId });
  const issueChatsQuery = useGetWatchIssueChatsQuery(publicId, { skip: !publicId });

  const [importWatchCompanies] = useImportWatchCompaniesMutation();
  const [createWatchCompanies] = useCreateWatchCompaniesMutation();
  const [openWatchCreateChat] = useOpenWatchCreateChatMutation();
  const [openWatchDossierChat] = useOpenWatchDossierChatMutation();
  const [openWatchIssueChat] = useOpenWatchIssueChatMutation();
  const [approveWatchInvestigation] = useApproveWatchInvestigationMutation();
  const [cancelWatchInvestigation] = useCancelWatchInvestigationMutation();
  const [continueWatchInvestigation] = useContinueWatchInvestigationMutation();
  const [retryWatchInvestigation] = useRetryWatchInvestigationMutation();
  const [addWatchKnownList] = useAddWatchKnownListMutation();
  const [createWatchSchedule] = useCreateWatchScheduleMutation();
  const [sendWatchDigest] = useSendWatchDigestMutation();

  const mapped = useMemo(() => mapWatchTowerLive({
    companies: companiesQuery.data,
    companyDetail: companyQuery.data,
    topics: topicsQuery.data,
    schedules: schedulesQuery.data,
    incidents: incidentsQuery.data,
    incidentDetail: incidentQuery.data,
    issueChats: issueChatsQuery.data,
  }), [
    companiesQuery.data,
    companyQuery.data,
    topicsQuery.data,
    schedulesQuery.data,
    incidentsQuery.data,
    incidentQuery.data,
    issueChatsQuery.data,
  ]);

  const [session, setSession] = useState<WatchTowerMockState | null>(null);

  const state = useMemo(() => mergeLiveState(mapped, session), [mapped, session]);

  const update = useCallback((
    updater: WatchTowerMockState | ((prev: WatchTowerMockState) => WatchTowerMockState),
  ) => {
    setSession((prev) => {
      const current = mergeLiveState(mapped, prev);
      return typeof updater === 'function' ? updater(current) : updater;
    });
  }, [mapped]);

  const reset = useCallback(() => {
    setSession(null);
    return emptyWatchTowerState();
  }, []);

  const liveActions: WatchTowerLiveActions = useMemo(() => ({
    importFile: async (payload) => {
      await importWatchCompanies(payload).unwrap();
    },
    openCreateChat: async () => {
      const bind = await openWatchCreateChat().unwrap();
      const chat = sessionCreateChat(bind);
      setSession((prev) => {
        const current = mergeLiveState(mapped, prev);
        return {
          ...current,
          createChats: upsertById(current.createChats, [chat]),
        };
      });
      return chat;
    },
    createCompanies: async (seed, confirmedExistingUids) => {
      await createWatchCompanies({ seed, confirmedExistingUids }).unwrap();
    },
    openIssueChat: async (finding) => {
      const bind = await openWatchIssueChat(finding.publicId).unwrap();
      const chat = sessionIssueChat(bind, finding);
      setSession((prev) => {
        const current = mergeLiveState(mapped, prev);
        return {
          ...current,
          issueChats: upsertById(current.issueChats, [chat]),
        };
      });
      return chat;
    },
    openDossierChat: async (id, companyName) => {
      const bind = await openWatchDossierChat(id).unwrap();
      const chat = sessionDossierChat(bind, { id, name: companyName });
      setSession((prev) => {
        const current = mergeLiveState(mapped, prev);
        return {
          ...current,
          createChats: upsertById(current.createChats, [chat]),
        };
      });
      return chat;
    },
    approveInvestigation: async (id) => {
      await approveWatchInvestigation(id).unwrap();
    },
    cancelInvestigation: async (id) => {
      await cancelWatchInvestigation(id).unwrap();
    },
    continueInvestigation: async (id, runId) => {
      await continueWatchInvestigation({ companyId: id, runId }).unwrap();
    },
    retryInvestigation: async (id) => {
      await retryWatchInvestigation(id).unwrap();
    },
    addKnownList: async (input) => {
      await addWatchKnownList(input).unwrap();
    },
    createSchedules: async (input, current) => {
      const jobs = input.companyIds.flatMap((companyIdValue) => (
        input.agentConfigIds.map((agentConfigId) => (
          createWatchSchedule(liveScheduleBody(input, current, companyIdValue, agentConfigId)).unwrap()
        ))
      ));
      await Promise.all(jobs);
    },
    sendDigest: async () => {
      await sendWatchDigest().unwrap();
    },
  }), [
    addWatchKnownList,
    approveWatchInvestigation,
    cancelWatchInvestigation,
    continueWatchInvestigation,
    createWatchCompanies,
    createWatchSchedule,
    importWatchCompanies,
    mapped,
    openWatchCreateChat,
    openWatchDossierChat,
    openWatchIssueChat,
    retryWatchInvestigation,
    sendWatchDigest,
  ]);

  const refreshOpenCompany = useCallback(() => {
    if (!companyId) {
      return Promise.resolve();
    }
    return refetchOpenCompany();
  }, [companyId, refetchOpenCompany]);

  return {
    state,
    update,
    reset,
    isLive: true as const,
    isLoading: companiesQuery.isLoading || incidentsQuery.isLoading,
    liveActions,
    refreshOpenCompany,
  };
}
